import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ContentsIconDefault } from '../../assets/images/contentsIconDefault';
import { TBL_AI_FEEDBACK_TABLE } from '../../constants/tblAiFeedbackTable';

const Recommendation = ({
    data = [],
    sx = {},
    userName = '',
    yesterdaySteps = null,
    avgHeartRate = null,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [selectedIndex, setSelectedIndex] = useState(null);

    // data가 배열인지 확인하고, 아니면 빈 배열로 변환
    const recommendationList = Array.isArray(data) ? data : (data?.recommendations?.[0]?.fitness || []);

    // 선택되지 않았을 때 표시할 메시지 계산
    const defaultMessage = useMemo(() => {
        let category = 0;
        let selectedMessages = [];

        // 디딤수 데이터가 없으면 카테고리 0
        if (yesterdaySteps === null || yesterdaySteps === undefined) {
            selectedMessages = TBL_AI_FEEDBACK_TABLE.filter(item => item.CATEGORY === 0);
        } else if (avgHeartRate !== null && avgHeartRate !== undefined && avgHeartRate > 0) {
            // 심박수 데이터가 있으면 (0이 아니면): 심박수 기반으로 카테고리 선택
            if (avgHeartRate < 75) {
                category = 1.4;
            } else if (avgHeartRate < 80) {
                category = 1.5;
            } else {
                category = 1.6;
            }
            selectedMessages = TBL_AI_FEEDBACK_TABLE.filter(item => item.CATEGORY === category);
        } else {
            // 심박수 데이터가 없거나 0이면: 디딤수 기반으로 카테고리 선택
            if (yesterdaySteps < 5000) {
                category = 1.1;
            } else if (yesterdaySteps < 9000) {
                category = 1.2;
            } else {
                category = 1.3;
            }
            selectedMessages = TBL_AI_FEEDBACK_TABLE.filter(item => item.CATEGORY === category);
        }

        if (selectedMessages.length === 0) {
            return null;
        }

        // 랜덤으로 메시지 선택: 상수는 원문 유지, MESSAGE_ID로 번역 키 조회 → 없으면 원문 폴백
        const randomMessage = selectedMessages[Math.floor(Math.random() * selectedMessages.length)];
        const messageKey = `ai_feedback_table_${String(randomMessage.MESSAGE_ID).toLowerCase()}_message`;
        let messageText = t(messageKey);
        if (messageText === messageKey) messageText = randomMessage.MESSAGE_TEXT || messageKey;

        // $님을 userName으로 치환
        if (userName) {
            messageText = messageText.replace(/\$/g, userName);
        } else {
            messageText = messageText.replace(/\$/g, '');
        }

        // #를 yesterdaySteps + 번역된 '보'(steps)로 치환
        if (yesterdaySteps !== null && yesterdaySteps !== undefined) {
            messageText = messageText.replace(/#/g, `${yesterdaySteps.toLocaleString()} ${t('steps')}`);
        }

        // &bpm을 avgHeartRate로 치환 (단위는 locale)
        if (avgHeartRate !== null && avgHeartRate !== undefined) {
            messageText = messageText.replace(/&bpm/g, `${avgHeartRate}${t('bpm')}`);
            messageText = messageText.replace(/&/g, `${avgHeartRate}`);
        }

        return messageText;
    }, [t, userName, yesterdaySteps, avgHeartRate]);

    return (
        <Box
            sx={{
                borderRadius: '20px',
                position: 'relative',
                width: '100%',
                padding: '4px',
                ...sx,
                // 그라데이션 테두리를 ::before 가상 요소로 적용
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '20px',
                    padding: '4px',
                    background: 'linear-gradient(109deg, #00B2FF 2.02%, #B0F 98.23%)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    pointerEvents: 'none',
                    zIndex: 0,
                },
            }}
        >
            <Box
                sx={{
                    borderRadius: '16px',
                    width: '100%',
                    background: theme.palette.figma?.block,
                    backgroundColor: theme.palette.figma?.block,
                    position: 'relative',
                    overflow: 'hidden',
                    zIndex: 1,
                }}
            >
                <Box
                    sx={{
                        padding: '20px',
                        width: '100%',
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        alignItems: 'center',
                    }}
                >
                    {/* Title */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            alignSelf: 'stretch',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '18px',
                                fontWeight: 500,
                                letterSpacing: '-0.54px',
                                textAlign: 'left',
                            }}
                        >
                            {t('ai_exercise_recommendation')}
                        </Typography>
                    </Box>

                    {/* Exercise Icons */}
                    {recommendationList.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '10px',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                alignSelf: 'stretch',
                                padding: '20px',
                                borderRadius: '10px',
                                backgroundColor: theme.palette.figma.white[10],
                                minHeight: '120px', // 최소 높이 설정하여 3개가 모두 보이도록
                            }}
                        >
                            {recommendationList.map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                        alignItems: 'center',
                                        flex: '1 0 0',
                                        minWidth: 0, // flex 아이템이 축소될 수 있도록
                                    }}
                                    onClick={() => setSelectedIndex(selectedIndex === index ? null : index)}
                                >
                                    <Box
                                        sx={{
                                            width: '60px',
                                            height: '60px',
                                            borderRadius: '61px',
                                            border: selectedIndex === index ? '4px solid ' + theme.palette.figma.buttonOrange : '1px solid ' + theme.palette.figma.white[30],
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            opacity: selectedIndex === null || selectedIndex === index ? 1 : 0.6,
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {item.icon ? (
                                            <img
                                                src={item.icon}
                                                alt={item.name}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        ) : (
                                            <ContentsIconDefault />
                                        )}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: selectedIndex === index ? 600 : 500,
                                            color: selectedIndex === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80],
                                            letterSpacing: '-0.36px',
                                            textAlign: 'center',
                                            lineHeight: 1.4,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            minHeight: '34px', // 최소 2줄 높이 (12px * 1.4 * 2 ≈ 34px)
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {item.name}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Messages - 선택된 아이콘에 해당하는 것만 표시, 또는 아무것도 선택하지 않았을 때 기본 메시지 */}
                    {selectedIndex !== null && recommendationList.length > 0 && recommendationList[selectedIndex] && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                alignSelf: 'stretch',
                            }}
                        >
                            {recommendationList[selectedIndex].circuit && (
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: theme.palette.figma.white[100],
                                        letterSpacing: '-0.36px',
                                        textAlign: 'left',
                                    }}
                                >
                                    {recommendationList[selectedIndex].circuit}
                                </Typography>
                            )}
                            {recommendationList[selectedIndex].comment && (
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        color: theme.palette.figma.white[100],
                                        letterSpacing: '-0.36px',
                                        textAlign: 'left',
                                        lineHeight: '1.5',
                                        minHeight: '36px', // 2줄 높이 고정 (12px * 1.5 * 2 = 36px)
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {recommendationList[selectedIndex].comment}
                                </Typography>
                            )}
                        </Box>
                    )}
                    {/* 아무것도 선택하지 않았을 때 기본 메시지 표시 */}
                    {(selectedIndex === null || recommendationList.length === 0) && defaultMessage && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                alignSelf: 'stretch',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    color: theme.palette.figma.white[100],
                                    letterSpacing: '-0.36px',
                                    textAlign: 'left',
                                    lineHeight: '1.5',
                                    minHeight: '36px', // 2줄 높이 고정 (12px * 1.5 * 2 = 36px)
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {defaultMessage}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Recommendation;
